import { constants } from "@/global/config/constants";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppStore } from "@/global/store";
import { selectCartManager } from "@/global/store/data/purchaseSeats/cartManager";
import React from "react";
import {
  injectMLBAdobeScript,
  parseCheckoutEventsData,
  pushAdobeDataLayer,
} from "./mlb-adobe-script-helpers";
import { routes } from "@/global/routes";
import { useSelectActiveEvent } from "@/shared/modules/PurchaseSeats/ChooseSeats/useSelectActiveEvent";

export function MLBAdobeScriptHeader() {
  const router = useRouterExtended();
  const teamConfig = useTeamConfig();
  const cartManager = useAppStore(selectCartManager);
  const { activeEvent } = useSelectActiveEvent();
  const [isScriptLoaded, setIsScriptLoaded] = React.useState(false);
  const teamId = String(teamConfig?.mlbTeamId || "");

  // ---------------------------------------------------
  // loading adobe script
  // ---------------------------------------------------
  React.useEffect(() => {
    (async () => {
      if (!isScriptLoaded) {
        await injectMLBAdobeScript();
        setIsScriptLoaded(true);
      }
    })();
  }, []);

  React.useEffect(() => {
    if (!isScriptLoaded) return;
    const tabNames = constants.purchaseSeatsTabUIDs;
    const urlPurchaseSeatsTab = router.query.purchaseSeatTab;

    // ---------------------------------------------------
    // find games page-view event
    // ---------------------------------------------------
    if (urlPurchaseSeatsTab == tabNames.findGames) {
      pushAdobeDataLayer({
        event: "page-view",
        page: {
          channel: "seasonshare",
          type: "product grid",
          custom_name: "find games",
        },
        baseball: {
          team_id: teamId,
        },
      });
    }

    // ---------------------------------------------------
    // choose seats page-view event
    // ---------------------------------------------------
    if (urlPurchaseSeatsTab == tabNames.chooseSeats && activeEvent) {
      pushAdobeDataLayer({
        event: "page-view",
        page: {
          channel: "seasonshare",
          type: "event detail",
          custom_name: "choose seats",
        },
        baseball: {
          team_id: teamId,
        },
      });
    }

    if (urlPurchaseSeatsTab == tabNames.checkout) {
      // ---------------------------------------------------
      // checkout page-view event
      // ---------------------------------------------------
      pushAdobeDataLayer({
        event: "page-view",
        page: {
          channel: "seasonshare",
          type: "checkout",
          custom_name: "checkout",
        },
        baseball: {
          team_id: teamId,
        },
      });
    }

    if (router.pathname === routes.checkoutStatus) {
      // ---------------------------------------------------
      // Confirmation/ThankYou page-view event
      // ---------------------------------------------------
      pushAdobeDataLayer({
        event: "page-view",
        page: {
          channel: "seasonshare",
          type: "confirmation",
        },
        baseball: {
          team_id: teamId,
        },
      });

      // ---------------------------------------------------
      // Confirmation/ThankYou purchase event
      // ---------------------------------------------------
      pushAdobeDataLayer({
        event: "purchase",
        ticketing: window?.adobeDataLayer?.find((itm: any) => {
          return itm?.event == "checkout-begin";
        })?.ticketing || { events: [] },
      });
    }
  }, [router, isScriptLoaded]);

  React.useEffect(() => {
    if (!isScriptLoaded) return;
    // ----------------------------------------------------------------------
    //  firing the product view event for each matchup/game/event in the cart
    // ----------------------------------------------------------------------
    if (activeEvent) {
      pushAdobeDataLayer({
        event: "product-view",
        ticketing: { events: [{ gamePk: activeEvent?.eventCode }] },
      });
    }
  }, [activeEvent, isScriptLoaded]);

  React.useEffect(() => {
    if (!isScriptLoaded) return;
    const tabNames = constants.purchaseSeatsTabUIDs;
    const urlPurchaseSeatsTab = router.query.purchaseSeatTab;

    // ---------------------------------------------------
    // checkout begin event
    // ---------------------------------------------------
    if (urlPurchaseSeatsTab === tabNames.checkout) {
      pushAdobeDataLayer({
        event: "checkout-begin",
        ticketing: {
          events: parseCheckoutEventsData(cartManager),
        },
      });
    }
  }, [cartManager, router, isScriptLoaded]);

  return <></>;
}
